import { useRef, useState, useCallback, useEffect } from 'react';
import { PhysicsWorld } from '../physics/world';
import { Body, createBody } from '../physics/body';
import { Vector2 } from '../physics/vector';
import { SCENARIOS } from '../physics/scenarios';
import { SavedWorld, loadSaves, saveWorld, deleteSave } from '../lib/storage';

export function useSimulation() {
  const worldRef = useRef<PhysicsWorld>(SCENARIOS[0].setup());
  const [bodies, setBodies] = useState<Body[]>(worldRef.current.bodies);
  const [running, setRunning] = useState(true);
  const [timeScale, setTimeScale] = useState(1);
  const [energy, setEnergy] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showPredictions, setShowPredictions] = useState(true);
  const [saves, setSaves] = useState<SavedWorld[]>([]);
  const rafRef = useRef<number>(0);
  const stepsRef = useRef(0);

  useEffect(() => {
    setSaves(loadSaves());
  }, []);

  const sync = useCallback(() => {
    const w = worldRef.current;
    setBodies([...w.bodies]);
    if (w.time % 10 === 0) {
      setEnergy(w.computeEnergy());
    }
  }, []);

  const tick = useCallback(() => {
    const w = worldRef.current;
    if (running) {
      const steps = Math.ceil(timeScale);
      for (let i = 0; i < steps; i++) {
        w.step();
      }
      stepsRef.current += steps;
      if (stepsRef.current % 5 === 0) {
        sync();
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [running, timeScale, sync]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [tick]);

  const loadScenario = useCallback((index: number) => {
    worldRef.current = SCENARIOS[index].setup();
    setBodies(worldRef.current.bodies);
    setEnergy(worldRef.current.computeEnergy());
    setSelectedId(null);
  }, []);

  const addBody = useCallback((pos: Vector2, vel: Vector2, mass: number, color: string) => {
    const body = createBody(pos, vel, mass, color);
    worldRef.current.addBody(body);
    setBodies([...worldRef.current.bodies]);
    return body;
  }, []);

  const removeBody = useCallback((id: number) => {
    worldRef.current.removeBody(id);
    setBodies([...worldRef.current.bodies]);
    if (selectedId === id) setSelectedId(null);
  }, [selectedId]);

  const clear = useCallback(() => {
    worldRef.current.clear();
    setBodies([]);
    setEnergy(0);
    setSelectedId(null);
  }, []);

  const saveCurrent = useCallback((name: string) => {
    const w = worldRef.current;
    const snapshot: SavedWorld = {
      name,
      timestamp: Date.now(),
      bodies: w.bodies.map((b) => ({
        pos: { x: b.pos.x, y: b.pos.y },
        vel: { x: b.vel.x, y: b.vel.y },
        mass: b.mass,
        color: b.color,
        fixed: b.fixed,
      })),
      config: { ...w.config },
    };
    saveWorld(snapshot);
    setSaves(loadSaves());
  }, []);

  const loadSave = useCallback((save: SavedWorld) => {
    const w = new PhysicsWorld(save.config);
    for (const b of save.bodies) {
      w.addBody(createBody(new Vector2(b.pos.x, b.pos.y), new Vector2(b.vel.x, b.vel.y), b.mass, b.color, b.fixed));
    }
    worldRef.current = w;
    setBodies(w.bodies);
    setEnergy(w.computeEnergy());
    setSelectedId(null);
  }, []);

  const removeSave = useCallback((index: number) => {
    deleteSave(index);
    setSaves(loadSaves());
  }, []);

  return {
    world: worldRef.current,
    bodies,
    running,
    setRunning,
    timeScale,
    setTimeScale,
    energy,
    selectedId,
    setSelectedId,
    showPredictions,
    setShowPredictions,
    saves,
    loadScenario,
    addBody,
    removeBody,
    clear,
    saveCurrent,
    loadSave,
    removeSave,
  };
}
